import { create } from "zustand";
import { persist } from "zustand/middleware";

export type ThemeMode = "system" | "light" | "dark";
type Theme = "light" | "dark";

interface ThemeState {
  // The user's preference. "system" means "follow the device" and is the
  // default — resolvedTheme tracks the OS live while this is set.
  mode: ThemeMode;
  // The actually-applied theme, derived from mode (+ the OS preference when
  // mode is "system"). Never persisted directly — recomputed on load and on
  // every OS theme-change event, so switching devices/settings takes effect
  // without a page reload.
  resolvedTheme: Theme;
  setMode: (mode: ThemeMode) => void;
  // Quick light/dark flip used by the icon-button toggle — an explicit
  // choice, so it always leaves "system" mode.
  toggleTheme: () => void;
}

function getSystemTheme(): Theme {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function resolveTheme(mode: ThemeMode): Theme {
  return mode === "system" ? getSystemTheme() : mode;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      mode: "system",
      resolvedTheme: resolveTheme("system"),
      setMode: (mode) => set({ mode, resolvedTheme: resolveTheme(mode) }),
      toggleTheme: () => {
        const next: Theme = get().resolvedTheme === "dark" ? "light" : "dark";
        set({ mode: next, resolvedTheme: next });
      }
    }),
    {
      name: "aurelia-theme",
      // Only the preference is persisted — resolvedTheme is always
      // recomputed so a stale "dark" doesn't survive an OS switch to light.
      partialize: (state) => ({ mode: state.mode }),
      onRehydrateStorage: () => (state) => {
        if (state) state.resolvedTheme = resolveTheme(state.mode);
      }
    }
  )
);

if (typeof window !== "undefined") {
  window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", (e) => {
    if (useThemeStore.getState().mode === "system") {
      useThemeStore.setState({ resolvedTheme: e.matches ? "dark" : "light" });
    }
  });
}

export function applyThemeClass(theme: Theme) {
  document.documentElement.classList.toggle("dark", theme === "dark");
}
